import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { PackageDetail } from '../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import { exceptionMessageHandler, PermissionType } from '$utils'
import { IClockinResponse, IStaffMemberDetail, ReportsMetadata } from '$models'
import { staffMemberService, timeClockinService } from '$services'
import { RemunerationType, ShiftType } from '$constants'
import { saveClockinAndPatchList } from '../_common/helpers'

interface TimeclockSharedState {
  allClockins: IClockinResponse[]
  staffMembers: IStaffMemberDetail[]
  allPackages: PackageDetail[]
}

export interface TimeclockSharedSlice extends TimeclockSharedState {
  setAllClockins: (newData: IClockinResponse[]) => void
  setStaffMembers: (newData: IStaffMemberDetail[]) => void
  setAllPackages: (newData: PackageDetail[]) => void

  /**
   * Resets Timeclock Shared Slice state
   */
  resetTimeclockSharedSlice: () => void

  /**
   * Gets time clockins within a certain time range
   * @param startTime the start of the time range
   * @param endTime the end of the time range
   * @param includeInactive whether or not to include inactive clocks
   * @param shiftTypesToInclude optional list of shift types to filter by
   * @param staffMemberIds the ids of the staff members to get the clocks for
   * @param shouldTruncateShifts Denotes if we should truncate the shifts or not
   */
  getTimeClockins: (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean,
    shiftTypesToInclude?: ShiftType[],
    staffMemberIds?: string[],
    shouldTruncateShifts?: boolean
  ) => Promise<IClockinResponse[]>

  /**
   * Get Staff Members
   * @param time Today's date (not UTC)
   * @param from Optional start date for remuneration timeline filtering (YYYY-MM-DD)
   * @param to Optional end date for remuneration timeline filtering (YYYY-MM-DD)
   */
  getStaffMembers: (time?: Dayjs, from?: string, to?: string) => Promise<void>

  /**
   * Get All Packages
   */
  getPackages: () => Promise<void>

  /**
   * Saves an edited clockin and puts the result back in the report
   * @param setRowLoading toggles a row loading state instead of the whole grid
   * @returns true when the save went through
   */
  updateClockinDetail: (
    clockinDetail: IClockinResponse,
    reportsMetadata?: ReportsMetadata,
    shiftTypesToInclude?: ShiftType[],
    includeInactive?: boolean,
    successMessage?: string,
    errorMessage?: string,
    setRowLoading?: (isLoading: boolean) => void
  ) => Promise<boolean>
}

const initialState: TimeclockSharedState = {
  allClockins: [],
  staffMembers: [],
  allPackages: [],
}

const createPunchesByStaffSlice: StateCreator<
  TimeclockSharedSlice & SharedSlice,
  [],
  [],
  TimeclockSharedSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  setAllClockins: (newData) => set(() => ({ allClockins: newData })),
  setStaffMembers: (newData) => set(() => ({ staffMembers: newData })),
  setAllPackages: (newData) => set(() => ({ allPackages: newData })),
  resetTimeclockSharedSlice: () => set(initialState),

  // State API calls
  getTimeClockins: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean,
    shiftTypesToInclude?: ShiftType[],
    staffMemberIds?: string[],
    shouldTruncateShifts: boolean = true
  ): Promise<IClockinResponse[]> => {
    return timeClockinService
      .getClockins(
        startDateTime,
        endDateTime,
        includeInactive,
        shiftTypesToInclude,
        staffMemberIds,
        shouldTruncateShifts
      )
      .then((result) => {
        if (staffMemberIds && staffMemberIds.length > 0) {
          // If specific staff member IDs are provided, merge with existing clockins
          set((state) => {
            // Filter out existing clockins for the specified staff members
            const filteredExistingClockins = state.allClockins.filter(
              (clockin) => !staffMemberIds.includes(clockin.staffMemberId!)
            )

            // Combine filtered existing clockins with new results
            const updatedClockins = [...filteredExistingClockins, ...result]

            return { allClockins: updatedClockins }
          })
        } else {
          // If no specific staff member IDs, replace all clockins
          set(() => ({ allClockins: result }))
        }
        return result
      })
      .catch((error) => {
        set(() => ({
          allClockins: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve time clockins')
        return []
      })
  },

  getStaffMembers: async (
    time?: Dayjs,
    from?: string,
    to?: string
  ): Promise<void> => {
    return staffMemberService
      .getAll(time, true, false, from, to)
      .then((result) => set(() => ({ staffMembers: result })))
      .catch((error) => {
        set(() => ({
          staffMembers: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({
          allPackages: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  updateClockinDetail: async (
    clockinDetail: IClockinResponse,
    reportsMetadata?: ReportsMetadata,
    shiftTypesToInclude?: ShiftType[],
    includeInactive: boolean = false,
    successMessage: string = 'The shift has been updated',
    errorMessage: string = 'Failed to update the shift',
    setRowLoading?: (isLoading: boolean) => void
  ) => {
    // A salary clockin can change the rate of the staff member's other entries in the
    // period, so refetch instead of patching the one row
    const refetchSalaryClockins =
      reportsMetadata &&
      clockinDetail.remunerationType === RemunerationType.Salary
        ? () =>
            get().getTimeClockins(
              reportsMetadata.startDateTime,
              reportsMetadata.endDateTime,
              includeInactive,
              shiftTypesToInclude,
              [clockinDetail.staffMemberId!]
            )
        : undefined

    return saveClockinAndPatchList({
      clockin: clockinDetail,
      getClockins: () => get().allClockins,
      setClockins: (allClockins) => set(() => ({ allClockins })),
      setLoading: setRowLoading ?? ((isLoading) => set(() => ({ isLoading }))),
      successMessage,
      errorMessage,
      refetchAfterSave: refetchSalaryClockins,
    })
  },
})

export default createPunchesByStaffSlice
